import * as React from 'react';

import { useSiteMetadata } from '../../utils/useSiteMetadata';
import { getMastodonHandleFromLink } from '../../utils/mastodon';

interface Props {
  title?: string;
  description?: string;
  pathname?: string;
  article?: boolean;
  dateISO?: string;
  authors?: readonly Queries.authorFieldsFragment[];
  tags?: CustomTypes.Tags;
  cover?: string | null;
  video?: string | null;
  socialCard?: string | null;
  authorMastodon?: string | null;
  authorTwitter?: string | null;
}

export const SEO = ({
  title,
  description,
  cover,
  video,
  socialCard,
  pathname,
  article,
  dateISO,
  authors,
  tags,
  authorMastodon,
  authorTwitter,
}: Props) => {
  const {
    author,
    title: defaultTitle,
    description: defaultDescription,
    name,
    image,
    logo,
    siteUrl,
    twitterUsername,
    mastodon,
    facebook,
    foundingDate,
  } = useSiteMetadata();

  const seo = {
    author,
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: `${siteUrl}${image}`,
    logo: `${siteUrl}${logo}`,
    url: `${siteUrl}${pathname || ''}`,
    twitterUsername,
    name,
    facebook,
    foundingDate,
  };

  const datePublished = dateISO && dateISO.split('T')[0];

  const coverUrl = cover ? `${siteUrl}${cover}` : undefined;
  const coverVideo = video ? `${siteUrl}${video}` : undefined;

  const seoImage = socialCard || coverUrl || seo.image;

  const mastodonHandles =
    authors
      ?.filter((author) => !!author.mastodon)
      .map((author) => getMastodonHandleFromLink(author.mastodon!)) || [];

  if (authorMastodon) {
    mastodonHandles.unshift(getMastodonHandleFromLink(authorMastodon));
  }

  const twitterHandle =
    authorTwitter || authors?.[0]?.twitter || seo.twitterUsername;

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />

      {/* Schema.org */}
      <meta itemProp="name" content={seo.title} />
      <meta itemProp="description" content={seo.description} />
      <meta itemProp="image" content={seoImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seo.twitterUsername} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seoImage} />

      {/* OpenGraph */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:image" content={seoImage} />
      {coverVideo && <meta property="og:video" content={coverVideo} />}
      <meta property="og:description" content={seo.description} />
      <meta property="og:site_name" content={seo.name} />
      <meta property="og:locale" content="en_US" />

      {mastodonHandles.length > 0 ? (
        mastodonHandles.map((handle) => (
          <meta name="fediverse:creator" key={handle} content={handle} />
        ))
      ) : (
        <meta
          name="fediverse:creator"
          content={getMastodonHandleFromLink(mastodon)}
        />
      )}

      {article && (
        <>
          <meta property="article:publisher" content={seo.facebook} />

          {dateISO && (
            <meta property="article:published_time" content={datePublished} />
          )}

          {authors &&
            authors.length > 0 &&
            authors
              .filter((author) => Boolean(author.facebook))
              .map((author) => (
                <meta
                  key={author.facebook}
                  property="article:author"
                  content={author.facebook || ''}
                />
              ))}

          {tags &&
            tags.length > 0 &&
            tags.map((tag, index) => (
              <React.Fragment key={tag.label}>
                {index === 0 && (
                  <meta property="article:section" content={tag.label} />
                )}

                <meta property="article:tag" content={tag.label} />
              </React.Fragment>
            ))}
        </>
      )}

      <link rel="icon" href="/favicon.ico" sizes="32x32" />
      <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.webmanifest" />
      <link href="https://cdn.iubenda.com" rel="preconnect" />
      <link href="https://www.googletagmanager.com" rel="preconnect" />
    </>
  );
};
